import { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Bot,
  User,
  Phone,
  MoreVertical,
  UserPlus,
  X as CloseIcon,
  RotateCcw,
  Lock,
  Unlock,
  Users,
  MessageCircle,
  Send,
  Globe,
  MessageSquare,
  Contact,
  ShoppingCart,
  Trash2,
} from 'lucide-react'

interface ConversationHeaderProps {
  conversation: {
    id: string
    contactName?: string
    waContact: string
    avatarUrl?: string
    status: 'open' | 'closed' | 'QUEUED'
    mode: 'bot' | 'human'
    channelType?: string
    waInstance?: {
      instanceName?: string
      profileName?: string
    }
  }
  otherUserTyping?: string | null
  isBotTyping?: boolean
  canTransferConversation?: boolean
  teams?: Array<{ id: string; name: string }>
  isStatusPending?: boolean
  isModePending?: boolean
  isTransferPending?: boolean
  isClearPending?: boolean
  onBack: () => void
  onOpenContact: () => void
  onOpenOrder?: () => void
  onToggleMode: (mode: 'bot' | 'human') => void
  onToggleStatus: (status: 'open' | 'closed') => void
  onTransfer?: (teamId: string) => void
  onClearMessages?: () => void
}

export function ConversationHeader({
  conversation,
  otherUserTyping,
  isBotTyping = false,
  canTransferConversation = false,
  teams = [],
  isStatusPending = false,
  isModePending = false,
  isTransferPending = false,
  isClearPending = false,
  onBack,
  onOpenContact,
  onOpenOrder,
  onToggleMode,
  onToggleStatus,
  onTransfer,
  onClearMessages,
}: ConversationHeaderProps) {
  const [showMenu, setShowMenu] = useState(false)

  // Cerrar menú con tecla Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showMenu) {
        setShowMenu(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showMenu])

  const getContactAvatar = (waContact: string, contactName?: string) => {
    if (contactName) {
      return contactName.slice(0, 2).toUpperCase()
    }
    const digits = waContact.replace(/\D/g, '').slice(-4)
    return digits.slice(0, 2)
  }

  const getAvatarColor = (waContact: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-teal-500',
    ]
    const index = waContact.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[index % colors.length]
  }

  const getChannelIcon = (channelType?: string) => {
    switch (channelType) {
      case 'WHATSAPP':
      case 'WHATSAPP_CLOUD':
        return <MessageCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
      case 'TELEGRAM':
        return <Send className="h-4 w-4 text-blue-500 flex-shrink-0" />
      case 'WEBCHAT':
        return <Globe className="h-4 w-4 text-gray-500 flex-shrink-0" />
      case 'MESSENGER':
        return <MessageSquare className="h-4 w-4 text-blue-600 flex-shrink-0" />
      case 'INSTAGRAM':
        return <MessageSquare className="h-4 w-4 text-pink-500 flex-shrink-0" />
      default:
        return <MessageCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
    }
  }

  const getChannelBadgeClass = (channelType?: string) => {
    switch (channelType) {
      case 'WHATSAPP':
      case 'WHATSAPP_CLOUD':
        return 'bg-emerald-100 text-emerald-700'
      case 'TELEGRAM':
        return 'bg-blue-100 text-blue-700'
      case 'WEBCHAT':
        return 'bg-gray-100 text-gray-700'
      case 'MESSENGER':
        return 'bg-blue-100 text-blue-700'
      case 'INSTAGRAM':
        return 'bg-pink-100 text-pink-700'
      default:
        return 'bg-emerald-100 text-emerald-700'
    }
  }

  const avatarColor = getAvatarColor(conversation.waContact)

  return (
    // Móvil (<640px): dos filas (identidad / badges + acciones) porque badges e iconos
    // no caben junto al nombre en 360px y se encimaban sobre él. sm+ mantiene la fila única.
    <div className="bg-white border-b px-2 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-1.5 sm:gap-y-0 flex-shrink-0 safe-area-top shadow-xs lg:sticky lg:top-0 lg:z-30">
      <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-1 min-w-0 w-full sm:w-auto">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors touch-target"
          aria-label="Volver a conversaciones"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>

        {conversation.avatarUrl ? (
          <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex-shrink-0 overflow-hidden ring-2 ring-gray-100">
            <img
              src={conversation.avatarUrl}
              alt={conversation.contactName || conversation.waContact}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div
            className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full ${avatarColor} flex items-center justify-center text-white font-semibold text-xs sm:text-sm lg:text-base flex-shrink-0 ring-2 ring-gray-100`}
          >
            {getContactAvatar(conversation.waContact, conversation.contactName)}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 lg:gap-2 min-w-0">
            {getChannelIcon(conversation.channelType)}
            {/* En móvil el canal ya se identifica por el icono: el texto (p.ej. WHATSAPP_CLOUD) tapaba el nombre */}
            <span
              className={`hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] lg:text-xs font-semibold ${getChannelBadgeClass(
                conversation.channelType
              )}`}
            >
              {conversation.channelType || 'WhatsApp'}
            </span>
            <h2 className="font-bold text-sm sm:text-base lg:text-lg text-gray-900 truncate min-w-0">
              {conversation.contactName || conversation.waContact}
            </h2>
          </div>

          {isBotTyping ? (
            <p className="text-[10px] sm:text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1.5 animate-pulse font-medium">
              <Bot className="h-3 w-3 flex-shrink-0" />
              <span className="inline-block w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full animate-bounce"></span>
              <span className="truncate">Bot escribiendo...</span>
            </p>
          ) : otherUserTyping ? (
            <p className="text-[10px] sm:text-xs text-emerald-600 flex items-center gap-1 animate-pulse">
              <span className="inline-block w-1.5 h-1.5 bg-emerald-600 rounded-full animate-bounce"></span>
              <span className="truncate">{otherUserTyping} escribiendo...</span>
            </p>
          ) : conversation.waInstance?.instanceName ? (
            <p className="text-[10px] sm:text-xs text-gray-500 truncate flex items-center gap-1">
              <Phone className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{conversation.waInstance.instanceName}</span>
            </p>
          ) : null}
        </div>
      </div>

      {/* Badges y Menú de Acciones: fila propia en móvil para no encimarse sobre el nombre */}
      <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-2.5 relative flex-shrink-0 w-full sm:w-auto justify-end">
        {/* Badge de Estado: Informativo */}
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] sm:text-xs font-semibold ${
            conversation.status === 'open'
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : conversation.status === 'QUEUED'
              ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : 'bg-rose-50 text-rose-700 border border-rose-200'
          }`}
        >
          {conversation.status === 'open' ? (
            <Unlock className="h-3 w-3" />
          ) : conversation.status === 'QUEUED' ? (
            <Users className="h-3 w-3" />
          ) : (
            <Lock className="h-3 w-3" />
          )}
          <span>
            {conversation.status === 'open'
              ? 'Abierta'
              : conversation.status === 'QUEUED'
              ? 'En cola'
              : 'Cerrada'}
          </span>
        </span>

        {/* Badge de Modo */}
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] sm:text-xs font-semibold ${
            conversation.mode === 'bot'
              ? 'bg-blue-50 text-blue-700 border border-blue-200'
              : 'bg-purple-50 text-purple-700 border border-purple-200'
          }`}
        >
          {conversation.mode === 'bot' ? (
            <Bot className="h-3 w-3" />
          ) : (
            <User className="h-3 w-3" />
          )}
          <span>{conversation.mode === 'bot' ? 'Bot' : 'Manual'}</span>
        </span>

        {/* Botón Ver Contacto */}
        <button
          type="button"
          onClick={onOpenContact}
          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors touch-target text-gray-600"
          aria-label="Ver perfil del contacto"
          title="Ver perfil del contacto"
        >
          <Contact className="h-5 w-5" />
        </button>

        {/* Botón Crear Orden */}
        <button
          type="button"
          onClick={onOpenOrder}
          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors touch-target text-gray-600"
          aria-label="Crear orden manual"
          title="Crear orden manual"
        >
          <ShoppingCart className="h-5 w-5" />
        </button>

        {/* Menú de Acciones Adicionales */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors touch-target text-gray-600"
            aria-label="Más opciones"
            aria-expanded={showMenu}
          >
            <MoreVertical className="h-5 w-5" />
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowMenu(false)}
              />
              <div
                role="menu"
                className="absolute right-0 top-11 z-50 w-56 rounded-xl border border-gray-200 bg-white py-1.5 shadow-xl"
              >
                {conversation.status !== 'closed' ? (
                  <>
                    {conversation.mode === 'bot' ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setShowMenu(false)
                          onToggleMode('human')
                        }}
                        disabled={isModePending}
                        className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <UserPlus className="h-4 w-4 text-purple-600" />
                        <span>Tomar control</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setShowMenu(false)
                          onToggleMode('bot')
                        }}
                        disabled={isModePending}
                        className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <Bot className="h-4 w-4 text-blue-600" />
                        <span>Devolver al Bot de ventas</span>
                      </button>
                    )}

                    <div className="my-1 border-t border-gray-100" />

                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setShowMenu(false)
                        onToggleStatus('closed')
                      }}
                      disabled={isStatusPending}
                      className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs sm:text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                    >
                      <CloseIcon className="h-4 w-4" />
                      <span>Cerrar conversación</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setShowMenu(false)
                      onToggleStatus('open')
                    }}
                    disabled={isStatusPending}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs sm:text-sm font-medium text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    <RotateCcw className="h-4 w-4" />
                    <span>Reabrir conversación</span>
                  </button>
                )}

                {canTransferConversation && teams.length > 0 && (
                  <>
                    <div className="my-1 border-t border-gray-100" />
                    <div className="px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      Mover a otro equipo
                    </div>
                    {teams.map((team) => (
                      <button
                        key={team.id}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setShowMenu(false)
                          onTransfer?.(team.id)
                        }}
                        disabled={isTransferPending}
                        className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="truncate">{team.name}</span>
                      </button>
                    ))}
                  </>
                )}

                {onClearMessages && (
                  <>
                    <div className="my-1 border-t border-gray-100" />
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setShowMenu(false)
                        onClearMessages()
                      }}
                      disabled={isClearPending}
                      className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-xs sm:text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4 text-rose-500" />
                      <span>Vaciar mensajes</span>
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
